/**
 * @fileoverview Org portal — the organisation's Hans plan (O5).
 *
 * Replaced OrgSeatsPage. An org used to buy N seats and hand them out; it now
 * buys the platform, and the plan covers every instructor who is a member. So
 * there is no quantity to choose, nothing to assign, and no cap to run into —
 * what an admin needs on this page is the bill, when it renews, and who it
 * covers.
 *
 * The bill itself is shown itemised on /org/usage beside the count that
 * produced it; here it is a single figure and a link, because an admin who
 * comes to this page is asking "what is my plan", not "check my arithmetic".
 */

import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { Card, SectionHeader, Button, Tag, ListRow } from "@/components/studio/primitives";

const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

const STATUS_LABELS = { active: "Active", trialing: "Trial", past_due: "Past due", canceled: "Canceled" };

function renewal(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export default function OrgSubscriptionPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [plan, setPlan] = useState(null);
  const [addOns, setAddOns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sub, addons] = await Promise.all([orgApi.get("/org/subscription"), orgApi.get("/org/addons")]);
      setPlan(sub.data);
      setAddOns(addons.data.add_ons || []);
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't load your plan"));
    } finally {
      setLoading(false);
    }
  }, []);

  // O7: buying and cancelling are the same control in two states, because to
  // an admin they are one decision about one line on their bill.
  const toggleAddOn = async (key, owned) => {
    setBusy(true);
    try {
      if (owned) await orgApi.delete(`/org/addons/${key}`);
      else await orgApi.post("/org/addons", { add_on: key });
      await load();
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't update your add-ons"));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const outcome = searchParams.get("checkout");
    if (!outcome) return;
    if (outcome === "success") toast.success("Thanks — Stripe is confirming your subscription.");
    if (outcome === "cancelled") toast("Checkout cancelled.");
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  const checkout = async () => {
    setBusy(true);
    try {
      const { data } = await orgApi.post("/org/subscription/checkout");
      window.location.href = data.url;
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't start checkout"));
      setBusy(false);
    }
  };

  const openPortal = async () => {
    setBusy(true);
    try {
      const { data } = await orgApi.post("/org/subscription/portal");
      window.location.href = data.url;
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't open billing"));
      setBusy(false);
    }
  };

  const active = ["active", "trialing"].includes(plan?.status);

  return (
    <div>
      <h1 className="mb-6 font-serif text-2xl font-semibold text-[var(--text-primary)]">Your plan</h1>

      <Card className="mb-6">
        <SectionHeader>Subscription</SectionHeader>
        {loading ? (
          <p className="py-2 font-sans text-[15px] text-[var(--text-tertiary)]">Loading…</p>
        ) : active ? (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="flex items-center gap-2">
                <span
                  className="font-sans text-[17px] font-semibold text-[var(--text-primary)]"
                  data-testid="org-plan-total"
                >
                  {money(plan.bill?.total_cents)} this month
                </span>
                <Tag accent>{STATUS_LABELS[plan.status] || plan.status}</Tag>
              </div>
              <p className="mt-1 font-sans text-[13px] text-[var(--text-tertiary)]" data-testid="org-plan-covers">
                Covers all {plan.covers_instructors} instructor
                {plan.covers_instructors === 1 ? "" : "s"} · {plan.active_students} active student
                {plan.active_students === 1 ? "" : "s"} this month
                {renewal(plan.period_end) ? ` · renews ${renewal(plan.period_end)}` : ""}
              </p>
            </div>
            {plan.manage_available && (
              <Button onPress={openPortal} disabled={busy} icon="creditCard" data-testid="org-plan-portal">
                Manage billing
              </Button>
            )}
          </div>
        ) : (
          <div>
            <p className="mb-4 font-sans text-[14px] text-[var(--text-secondary)]">
              One subscription for your whole organization. Every instructor you invite is covered — there is nothing to
              assign and no seat to run out of. You pay a base each month plus a rate per student actually taught.
            </p>
            {plan?.over_self_serve_ceiling ? (
              /* O14: above the ceiling the deal is worth negotiating anyway, so
                 a checkout is the wrong instrument — say so plainly rather
                 than let somebody click into a 409. */
              <p className="font-sans text-[13px] text-[var(--text-tertiary)]" data-testid="org-plan-contact-sales">
                You&apos;re teaching more than {plan.self_serve_ceiling} students. At your size we set the plan up with
                you directly — get in touch and we&apos;ll sort it out.
              </p>
            ) : plan?.purchase_available ? (
              <Button onPress={checkout} disabled={busy} data-testid="org-plan-checkout">
                {busy ? "Opening checkout…" : "Set up billing"}
              </Button>
            ) : (
              <p className="font-sans text-[13px] text-[var(--text-tertiary)]" data-testid="org-plan-unavailable">
                Self-serve purchase isn&apos;t available yet — get in touch and we&apos;ll set your plan up for you.
              </p>
            )}
          </div>
        )}
      </Card>

      {addOns.length > 0 ? (
        <Card className="mb-6">
          <SectionHeader>Add-ons</SectionHeader>
          <div className="-mx-[18px]">
            {addOns.map((a, i) => (
              <ListRow
                key={a.key}
                borderBottom={i < addOns.length - 1}
                title={a.label}
                subtitle={
                  a.required_by.length
                    ? "Needed by another add-on you own"
                    : a.requires && !addOns.find((x) => x.key === a.requires)?.owned
                      ? `Requires ${addOns.find((x) => x.key === a.requires)?.label}`
                      : a.waived_on_annual
                        ? "Included free on annual billing"
                        : null
                }
                meta={`${money(a.monthly_cents)}/mo`}
                trailing={
                  <div className="flex items-center gap-2">
                    {a.owned ? <Tag accent>Active</Tag> : null}
                    <button
                      type="button"
                      onClick={() => toggleAddOn(a.key, a.owned)}
                      disabled={
                        busy ||
                        (a.owned && a.required_by.length > 0) ||
                        (!a.owned && !!a.requires && !addOns.find((x) => x.key === a.requires)?.owned)
                      }
                      data-testid={`org-addon-${a.key}`}
                      className={[
                        "font-sans text-[13px] font-medium hover:underline",
                        "disabled:opacity-40 disabled:no-underline",
                        a.owned ? "text-[var(--state-danger)]" : "text-[var(--text-secondary)]",
                      ].join(" ")}
                    >
                      {a.owned ? "Cancel" : "Add"}
                    </button>
                  </div>
                }
              />
            ))}
          </div>
          <p className="mt-4 font-sans text-[13px] text-[var(--text-tertiary)]">
            Add or cancel at any time — you&apos;re charged for what you use, to the day. Cancelling keeps anything
            you&apos;ve set up, like a logo; it just stops being applied.
          </p>
        </Card>
      ) : null}

      {plan?.bill ? (
        <Card>
          <SectionHeader>What you&apos;re billed for</SectionHeader>
          <p className="font-sans text-[14px] text-[var(--text-secondary)]">
            {money(plan.bill.base_cents)} base, covering your first {plan.bill.included_students} active students, then
            a rate per student above that. A student counts once they have had a lesson recorded in one of your
            programs.
          </p>
          <p className="mt-3 font-sans text-[13px]">
            <Link to="/org/usage" className="text-[var(--accent-yellow-text)]" data-testid="org-plan-usage-link">
              See who was counted this month →
            </Link>
          </p>
        </Card>
      ) : null}
    </div>
  );
}
